import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, CheckCircle2, Circle, Trophy, ClipboardList, AlertTriangle, AlertCircle, Info } from "lucide-react";

/**
 * ProfileChecklist — CAS-style aviation checklist.
 *   RED (WARNING)  — first 2 open tasks: identity/legal. "Do this now."
 *   AMBER (CAUTION) — next 2 open tasks: operational. "Do this soon."
 *   BLUE (ADVISORY) — remaining open tasks: enhancements. "Do this eventually."
 * Completed tasks turn green (NORMAL) with strikethrough.
 */
function casStyleForTask(task, openIndex) {
  if (task.done) return { tone: "avail", label: "OK",       ring: "border-avail/30",       text: "text-white/40 line-through", chip: "bg-avail/10 text-avail border-avail/40" };
  if (openIndex === 0) return { tone: "red",   label: "WARN", ring: "border-red-500/50 animate-pulse-slow", text: "text-red-300",   chip: "bg-red-500/15 text-red-400 border-red-500/40" };
  if (openIndex === 1) return { tone: "red",   label: "WARN", ring: "border-red-500/40",                    text: "text-red-300",   chip: "bg-red-500/15 text-red-400 border-red-500/40" };
  if (openIndex <= 3)  return { tone: "amber", label: "CAUT", ring: "border-amber-400/40",                  text: "text-amber-300",  chip: "bg-amber-400/15 text-amber-300 border-amber-400/40" };
  return                        { tone: "blue",  label: "ADV",  ring: "border-cyan/30",                       text: "text-cyan/90",   chip: "bg-cyan/10 text-cyan border-cyan/40" };
}

export default function ProfileChecklist({ compact = false, mini = false, title = "Onboarding Checklist" }) {
  const [state, setState] = useState(null);

  const load = () => api.get("/profile/checklist").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!state) return null;
  const done = state.completed;
  const total = state.total;
  const pct = state.percent || 0;

  // Mini variant — one-line progress bar sized to slot next to the PHF
  // block inside the FAA identity card. No task list, no CAS colors,
  // just: count + progress + link to the full checklist above.
  if (mini) {
    const nextOpen = state.tasks.find((t) => !t.done);
    return (
      <div
        className="rounded-lg border border-white/[0.10] bg-white/[0.03] backdrop-blur-xl p-3"
        data-testid="profile-checklist-mini"
      >
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-sky/80">// Checklist</div>
          <div className="mono text-[10px] tracking-widest text-white/70">
            <span className="text-white font-bold">{done}</span>
            <span className="text-white/40">/{total}</span>
            <span className={`ml-2 ${pct === 100 ? "text-avail" : "text-sky"}`}>{pct}%</span>
          </div>
        </div>
        <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${pct}%`, background: pct === 100 ? "#2ED573" : "#4B9CFF", boxShadow: `0 0 8px ${pct === 100 ? "#2ED573" : "#4B9CFF"}` }}
          />
        </div>
        {pct === 100 ? (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-avail">
            <Trophy className="h-3 w-3" /> All squared away, Captain.
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="text-[11px] text-white/60 truncate" title={nextOpen?.label}>
              Next: <span className="text-white/85">{nextOpen?.label || "—"}</span>
            </div>
            {nextOpen?.cta ? (
              <Link
                to={nextOpen.cta.href}
                data-testid="profile-checklist-mini-cta"
                className="inline-flex items-center gap-1 mono text-[9px] tracking-widest uppercase text-sky hover:text-white flex-shrink-0"
              >
                {nextOpen.cta.label} <ChevronRight className="h-3 w-3" />
              </Link>
            ) : null}
          </div>
        )}
      </div>
    );
  }

  // Border color = highest severity currently open.
  const openTasks = state.tasks.filter((t) => !t.done);
  const border = openTasks.length === 0
    ? "!border-avail/60 shadow-[0_0_25px_rgba(46,213,115,0.15)]"
    : openTasks.length <= 2
      ? "!border-red-500/60 shadow-[0_0_25px_rgba(239,68,68,0.18)]"
      : openTasks.length <= 4
        ? "!border-amber-400/60 shadow-[0_0_25px_rgba(251,191,36,0.20)]"
        : "!border-cyan/60 shadow-[0_0_25px_rgba(75,156,255,0.15)]";

  return (
    <div className={`card !p-4 sm:!p-5 !border-[3px] ${border} transition-all`} data-testid="profile-checklist">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-sky/80">// PJP · CHECKLIST</div>
          <div className="font-display font-semibold text-white text-lg sm:text-xl mt-0.5">{title}</div>
          <div className="text-white/55 text-xs mt-0.5">Aviation loves checklists — complete yours to unlock full PJP.</div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-display font-bold text-2xl text-white leading-none">{done}<span className="text-white/40 text-base">/{total}</span></div>
          <div className="mono text-[10px] tracking-widest text-avail uppercase">{pct}% DONE</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden mb-3">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: pct === 100 ? "#2ED573" : "#4B9CFF", boxShadow: `0 0 8px ${pct === 100 ? "#2ED573" : "#4B9CFF"}` }}
        />
      </div>

      {pct === 100 ? (
        <div className="rounded-xl border border-avail/40 bg-avail/[0.08] p-4 flex items-start gap-3" data-testid="checklist-complete">
          <Trophy className="h-5 w-5 text-avail flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-semibold text-white">All squared away, Captain.</div>
            <div className="text-white/60 text-sm mt-0.5">Your profile is fully unlocked. Keep the ops network humming.</div>
          </div>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {(compact ? state.tasks.slice(0, 5) : state.tasks).map((t) => (
            <li key={t.key} className="flex items-start gap-3 py-1.5" data-testid={`task-${t.key}`}>
              <span className="flex-shrink-0 mt-0.5">
                {t.done ? (
                  <CheckCircle2 className="h-4 w-4 text-avail" style={{ fill: "rgba(46,213,115,0.15)" }} />
                ) : (
                  <Circle className="h-4 w-4 text-white/30" />
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${t.done ? "text-white/40 line-through" : "text-white/90"}`}>
                  <span className="mono text-white/40 mr-1.5">{String(t.n).padStart(2, "0")}.</span>
                  {t.label}
                </div>
              </div>
              {!t.done && t.cta && (
                <Link
                  to={t.cta.href}
                  data-testid={`task-cta-${t.key}`}
                  className="inline-flex items-center gap-1 text-[11px] mono font-bold tracking-widest text-sky hover:text-white flex-shrink-0 uppercase min-h-[32px] px-2"
                >
                  {t.cta.label}
                  <ChevronRight className="h-3 w-3" />
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}

      {compact && state.tasks.length > 5 && pct !== 100 && (
        <div className="mt-3 pt-3 border-t border-white/[0.06]">
          <Link to="/profile" className="mono text-[10px] tracking-widest text-sky hover:text-white uppercase flex items-center gap-1">
            <ClipboardList className="h-3 w-3" />
            View all {state.tasks.length} tasks
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
